import { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import throttle from "lodash/throttle";
import { Button } from "../Button";
import { ArrowUpIcon } from "../Svg";

const FixedContainer = styled.div`
  position: fixed;
  right: 5%;
  bottom: calc(110px + env(safe-area-inset-bottom));
`;

/**
 * Elements the floating button must never cover (trade panels, quote cards, confirm rows) opt in
 * with `data-fab-avoid`; while the button's box overlaps one of them, or any dialog is open, the
 * button is hidden (visibility, so it keeps its box and re-appears as soon as the page scrolls on).
 */
const AVOID_SELECTOR = "[data-fab-avoid]";
const DIALOG_SELECTOR = '[role="dialog"]';
const GAP = 8;

function overlapsAvoided(box: DOMRect) {
  const nodes = document.querySelectorAll(AVOID_SELECTOR);
  for (let i = 0; i < nodes.length; i++) {
    const r = nodes[i].getBoundingClientRect();
    if (
      r.width > 0 &&
      r.height > 0 &&
      r.left < box.right + GAP &&
      r.right > box.left - GAP &&
      r.top < box.bottom + GAP &&
      r.bottom > box.top - GAP
    )
      return true;
  }
  return false;
}

function dialogOpen() {
  const nodes = document.querySelectorAll(DIALOG_SELECTOR);
  for (let i = 0; i < nodes.length; i++) {
    const r = nodes[i].getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return true;
  }
  return false;
}

const ScrollToTopButtonV2 = () => {
  const [visible, setVisible] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const toggleVisible = () => {
      const scrolled = document.documentElement.scrollTop;
      if (scrolled > 500) {
        setVisible(true);
      } else if (scrolled <= 500) {
        setVisible(false);
      }
    };

    const throttledToggleVisible = throttle(toggleVisible, 200);

    window.addEventListener("scroll", throttledToggleVisible);

    return () => window.removeEventListener("scroll", throttledToggleVisible);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const check = () => {
      const el = ref.current;
      if (!el) return;
      setBlocked(dialogOpen() || overlapsAvoided(el.getBoundingClientRect()));
    };
    const throttled = throttle(check, 100);
    check();
    window.addEventListener("scroll", throttled);
    window.addEventListener("resize", throttled);
    // dialogs and trade panels mount without a scroll event
    const observer = new MutationObserver(throttled);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      throttled.cancel();
      window.removeEventListener("scroll", throttled);
      window.removeEventListener("resize", throttled);
      observer.disconnect();
    };
  }, [visible]);

  return (
    <FixedContainer
      ref={ref}
      data-testid="scroll-to-top"
      data-blocked={blocked ? "true" : undefined}
      style={{ display: visible ? "inline" : "none", visibility: blocked ? "hidden" : "visible" }}
    >
      <Button
        width={48}
        height={48}
        endIcon={<ArrowUpIcon color="invertedContrast" style={{ marginLeft: 0 }} />}
        onClick={scrollToTop}
        aria-hidden={blocked || undefined}
        tabIndex={blocked ? -1 : undefined}
      />
    </FixedContainer>
  );
};

export default ScrollToTopButtonV2;
